import _ from 'lodash';
import InvoicesService from "../InvoicesService";
import InvoiceSearch from "../models/InvoiceSearch.model";
import EventBus from "@/plugins/event-bus";
import {ON_INVOICES_TABLE_GET_TABLE_DATA} from "@/plugins/event-bus/event-bus-names";
import {
  INVOICE_TABLE_NAME,
  INVOICE_TYPE_AUCTION,
  INVOICE_TYPE_MANUAL,
  INVOICE_TYPE_CREDIT_NOTE
} from "@/modules/constants";
import {store} from "@/plugins/vuex";
import i18n from "../../../shared/i18n";
import {hasPermission} from "@/core/directives/permission";

let getDefaultTableData = (differentFields = []) => {
  return {
    loading: false,
    customer: null,
    rows: [],
    pagination: {
      limit: 50,
      page: 1,
      total: 0,
    },
    ordering: '-invoice_no',
    search: '',
    searchModel: new InvoiceSearch(),
    extraFilters: {},
    expand: [
      'auction.currency',
      'customer.billing_address.country',
      'total_amount',
      'total_lots',
      'invoice_payments.payment.currency',
      'invoice_payments.payment.batch_payment',
      'credited_invoice',
      'currency',
      'total_amount',
      'outstanding_amount',
      'paid_amount',
      'lot',
      'credit_note',
      'invoice_payments.financial_export.created_by',
    ],
    toggleColumns: {
      fields: [...getDefaultFields(), ...differentFields],
      dynamicFields: [],
      requiredFields: ['invoice_no'],
      defaultFields: ['invoice_no', 'type', 'sale_no', 'customer_uuid', 'amount', 'items', 'status', 'date', 'shipping_status', 'actions']
    }
  }
};

let getDefaultManualInvoiceModel = () => {
  return {
    show: false,
    object: null,
    source: null,
    customer: null,
    singleInventoryItem: null,
    creditNoteWithoutRefundParams: null
  }
}

let getDefaultServiceInvoiceModal = () => {
  return {
    show: false,
    object: null,
    source: null,
    customer: null,
    creditNoteWithoutRefundParams: null
  }
}

let getDefaultDepositInvoiceModel = () => {
  return {
    show: false,
    object: null,
    source: null,
    customer: null,
    creditNoteWithoutRefundParams: null
  }
}

let getDefaultContinuousAuctionInvoiceModal = () => {
  return {
    show: false,
    object: null,
    source: null,
    sale: null,
    creditNoteWithoutRefundParams: null
  }
}

let getDefaultCreditNoteModel = () => {
  return {
    show: false,
    object: null,
    source: null
  }
}

let getDefaultBulkEditInvoicesModel = () => {
  return {
    show: false,
    invoices: []
  }
}

let getDefaultFields = () => {
  let fields = [
    {
      key: 'invoice_no',
      label: i18n.t('Invoice'),
      sortable: true,
      tdClass: 'invoice-no-col',
      isVisible: true,
      expand: ''
    },
    {key: 'type', label: i18n.t('Type'), sortable: true, tdClass: 'type-col', isVisible: true, expand: ''},
    {key: 'sale_no', label: i18n.t('Sale'), sortable: true, tdClass: 'sale-no-col', isVisible: true, expand: 'auction'},
    {
      key: 'customer_uuid',
      label: i18n.t('Buyer'),
      sortable: true,
      tdClass: 'customer-uuid-col',
      isVisible: true,
      expand: 'customer.term'
    },
    {key: 'tags', label: i18n.t('Tags'), sortable: false, isVisible: false, tdClass: 'tags-col', expand: 'customer'},
    {
      key: 'total_buyer_vat',
      label: i18n.t('Buyer VAT'),
      sortable: true,
      tdClass: 'buyer-vat-col',
      isVisible: true,
      expand: ''
    },
    {
      key: 'customer_company_name',
      label: i18n.t('Buyer Company'),
      sortable: true,
      tdClass: 'customer_company_name-col',
      isVisible: false,
      expand: 'customer'
    },
    {
      key: 'amount',
      label: i18n.t('Total / Paid / Outstanding'),
      sortable: true,
      tdClass: 'amount-col',
      isVisible: true,
      expand: 'currency,total_amount,outstanding_amount,paid_amount'
    },
    {key: 'items', label: i18n.t('Items'), sortable: true, tdClass: 'items-col', isVisible: true, expand: ''},
    {key: 'status', label: i18n.t('Status'), sortable: true, tdClass: 'status-col', isVisible: true, expand: ''},
    {key: 'date', label: i18n.t('Raised'), sortable: true, tdClass: 'date-col', isVisible: true, expand: 'is_overdue'},
    {
      key: 'shipping_status',
      label: i18n.t('Shipping'),
      sortable: true,
      tdClass: 'shipping-status-col',
      isVisible: true,
      expand: 'shipping_status'
    },
    {
      key: 'shipping_tracking_code',
      label: i18n.t('Tracking Code'),
      sortable: true,
      tdClass: 'shipping_tracking_code-col',
      isVisible: false,
      expand: 'shipping_status'
    },
    {
      key: 'shipping_address_1',
      label: i18n.t('Shipping Address 1'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_shipping_address'
    },
    {
      key: 'shipping_address_2',
      label: i18n.t('Shipping Address 2'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_shipping_address'
    },
    {
      key: 'shipping_state',
      label: i18n.t('Shipping State'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_shipping_address'
    },
    {
      key: 'shipping_county',
      label: i18n.t('Shipping County'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_shipping_address.country'
    },
    {
      key: 'shipping_city',
      label: i18n.t('Shipping City'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_shipping_address'
    },
    {
      key: 'shipping_zip_code',
      label: i18n.t('Shipping Zip/Post Code'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_shipping_address'
    },
    {
      key: 'shipping_country',
      label: i18n.t('Shipping Country'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_shipping_address.country'
    },
    {
      key: 'shipping_first_name',
      label: i18n.t('Shipping First Name'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_shipping_address'
    },
    {
      key: 'shipping_last_name',
      label: i18n.t('Shipping Last Name'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_shipping_address'
    },
    {
      key: 'billing_address_1',
      label: i18n.t('Billing Address 1'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_billing_address'
    },
    {
      key: 'billing_address_2',
      label: i18n.t('Billing Address 2'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_billing_address'
    },
    {
      key: 'billing_state',
      label: i18n.t('Billing State'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_billing_address'
    },
    {
      key: 'billing_county',
      label: i18n.t('Billing County'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_billing_address.country'
    },
    {
      key: 'billing_city',
      label: i18n.t('Billing City'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_billing_address'
    },
    {
      key: 'billing_zip_code',
      label: i18n.t('Billing Zip/Post Code'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_billing_address'
    },
    {
      key: 'billing_country',
      label: i18n.t('Billing Country'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_billing_address.country'
    },
    {
      key: 'billing_first_name',
      label: i18n.t('Billing First Name'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_billing_address'
    },
    {
      key: 'billing_last_name',
      label: i18n.t('Billing Last Name'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'invoice_billing_address'
    },

    {
      key: 'last_synced_at',
      label: i18n.t('Synced At'),
      sortable: true,
      tdClass: 'synced-at-col',
      isVisible: false,
      expand: ''
    },
    {
      key: 'collected',
      label: i18n.t('Collected'),
      displayName: i18n.t('Collected'),
      sortable: true, tdClass: 'collected-col',
      isVisible: false,
      expand: 'total_lots,total_collected_lots'
    },
    {
      key: "complaint_status",
      label: i18n.t('Complaint'),
      sortable: true,
      tdClass: 'complaint-col',
      isVisible: false,
      expand: ''
    },
    {
      key: 'computed_auto_payment_status',
      label: i18n.t('Status of Auto-Payments'),
      sortable: false,
      tdClass: 'auto-payment-status-col',
      isVisible: false,
      expand: 'computed_auto_payment_status'
    },
    {
      key: 'actions',
      label: i18n.t('Actions'),
      sortable: false,
      tdClass: 'actions-col',
      isVisible: true,
      expand: 'total_notes',
      exclude_from_export: true
    },
    {
      key: 'overdue_notification_send_at',
      label: i18n.t('Overdue Email Sent At'),
      sortable: true,
      tdClass: 'overdue-notification-not-sent-col',
      isVisible: false,
      expand: ''
    },
    {
      key: 'unpaid_notification_send_at',
      label: i18n.t('Unpaid Email Sent At'),
      sortable: true,
      tdClass: 'unpaid-notification-not-sent-col',
      isVisible: false,
      expand: ''
    },
    {
      key: 'is_overdue',
      label: i18n.t('Overdue'),
      sortable: true,
      tdClass: 'is-overdue-col',
      isVisible: false,
      expand: 'is_overdue'
    },
    {
      key: 'payment_methods',
      label: i18n.t('Payment Methods'),
      sortable: false,
      tdClass: 'payment_methods_col',
      isVisible: false,
      expand: 'payment_methods'
    },
    {
      key: 'created_data',
      label: i18n.t('Created at / Created by'),
      sortable: false,
      tdClass: 'created-data-col',
      isVisible: false,
      expand: 'created_by'
    },
    {
      key: 'updated_data',
      label: i18n.t('Updated at / Updated by'),
      sortable: false,
      tdClass: 'updated-data-col',
      isVisible: false,
      expand: 'updated_by'
    },
    {
      key: 'collection_info_contract_status',
      label: i18n.t('Collection Note'),
      sortable: false,
      tdClass: 'collection_info_contract_status-col',
      isVisible: false,
      expand: 'collection_info_contract_status'
    },
    {
      key: 'approved',
      label: i18n.t('Approved'),
      sortable: true,
      tdClass: 'approved-col',
      isVisible: false,
      expand: ''
    },
    {
      key: 'paid_at',
      label: i18n.t('Paid Date'),
      sortable: true,
      tdClass: 'paid-at-col',
      isVisible: false,
      expand: ''
    },
    {
      key: 'transaction_history',
      label: i18n.t('Transaction History'),
      sortable: true,
      tdClass: 'transaction_history-col',
      isVisible: false,
      expand: 'invoice_tr_history.financial_export.created_by,invoice_tr_history.created_by',
      exclude_from_export: true
    },
    {
      key: 'payment_history',
      label: i18n.t('Payment History'),
      sortable: true,
      tdClass: 'payment_history-col',
      isVisible: false,
      expand: '',
      exclude_from_export: true
    },
    {
      key: 'identity_verification',
      label: i18n.t('Identity Verification'),
      sortable: true,
      tdClass: '',
      isVisible: false,
      expand: 'customer.term'
    },
    {
      key: 'kyc_status_verification',
      label: i18n.t('Account/KYC Verification'),
      sortable: true,
      tdClass: '',
      isVisible: false,
      expand: 'customer.term'
    },
    {
      key: 'payment_terms',
      label: i18n.t('Payment Terms'),
      sortable: true,
      tdClass: '',
      isVisible: false,
      expand: 'customer.term'
    },
    {
      key: "collector",
      label: i18n.t('Collector'),
      sortable: false,
      tdClass: 'specialists-col',
      isVisible: false,
      expand: ''
    },
    {
      key: 'customer_email',
      label: i18n.t('Buyer Email'),
      sortable: false,
      tdClass: 'customer_company_name-col',
      isVisible: false,
      expand: 'customer'
    },
    {
      key: 'credited_invoice',
      label: i18n.t('Associated Credited Invoice'),
      sortable: false,
      tdClass: 'credit_invoice-col',
      isVisible: false,
      expand: 'credited_invoice'
    },
    {
      key: 'credit_note',
      label: i18n.t('Associated Credit Note'),
      sortable: false,
      tdClass: 'credit_note-col',
      isVisible: false,
      expand: 'credit_note'
    },
    {
      key: 'buyer_group_account',
      label: i18n.t('Buyer Group Account'),
      sortable: false,
      isVisible: false,
      expand: 'auction'
    },
    {
      key: 'client_tier',
      label: i18n.t('Client Tier'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'customer'
    },
    {
      key: 'exclude_from_auto_payments',
      label: i18n.t('Exclude from Auto-Payments'),
      sortable: true,
      isVisible: false,
      expand: ''
    },
    {
      key: 'sale_sources',
      label: i18n.t('Auction Provider(s)'),
      sortable: false,
      isVisible: false,
      expand: 'sale_sources'
    },
    {
      key: 'collection_appointment_notification_sent_at',
      label: i18n.t('Collection Appointment Email Sent At'),
      sortable: true,
      tdClass: 'collection-appointment-email-sent-at-col',
      isVisible: false,
      expand: ''
    },
    {
      key: 'total_amount',
      label: i18n.t('Total Amount'),
      sortable: true,
      isVisible: false,
      expand: ''
    },
    {
      key: 'paid_amount',
      label: i18n.t('Paid Amount'),
      sortable: true,
      isVisible: false,
      expand: ''
    },
    {
      key: 'outstanding_amount',
      label: i18n.t('Outstanding Amount'),
      sortable: true,
      isVisible: false,
      expand: ''
    },
    {
      key: 'branch',
      label: i18n.t('Branch'),
      sortable: false,
      tdClass: '',
      isVisible: false,
      expand: 'branch'
    },
  ]
  const excludeFields = [];
  if (!hasPermission('view_customeridentification')) {
    excludeFields.push('identity_verification');
    excludeFields.push('kyc_status_verification');
  }
  if (!hasPermission('view_customerbankaccount') || !store || !store.state.global || !store.state.global.hasStripeProvider) {
   excludeFields.push('exclude_from_auto_payments');
   excludeFields.push('computed_auto_payment_status');
  }
  if (excludeFields.length) {
    fields = fields.filter(f => !excludeFields.includes(f.key));
  }
  return fields
}

export default {
  namespaced: true,
  state: {
    currentSale: null,
    modal: {
      show: false,
      object: null,
      source: null,
      sale: null,
      creditNoteWithoutRefundParams: null
    },
    serviceInvoiceModal: getDefaultServiceInvoiceModal(),
    manualInvoiceModal: getDefaultManualInvoiceModel(),
    depositInvoiceModal: getDefaultDepositInvoiceModel(),
    continuousAuctionInvoiceModal: getDefaultContinuousAuctionInvoiceModal(),
    shippingModal: {
      show: false,
      object: null
    },
    paymentModal: {
      show: false,
      type: 'payment',
      object: null,
      source: null,
      invoices: []
    },
    cancelInvoiceModal: {
      show: false,
      source: null,
      selectedItems: null
    },
    paymentListModal: {
      show: false,
      source: null,
      invoice: {}
    },
    tableSource: null,
    tableData: {
      sale: getDefaultTableData(),
      customer: getDefaultTableData(),
      invoices: getDefaultTableData(),
      shipping: getDefaultTableData()
    },
    markItemsCollectedModal: {
      show: false,
      source: null,
      object: null
    },
    transactionExportsModal: {
      show: false,
      source: null,
      invoice: null,
    },
    paymentExportsModal: {
      show: false,
      source: null,
      invoice: null,
    },
    bulkEditInvoicesModal: getDefaultBulkEditInvoicesModel(),
    creditNoteModal: getDefaultCreditNoteModel(),
    allocatePaymentModal: {
      show: false,
      sourceInvoice: null,
      loading: false,
      source: null
    },
    invoiceBatchPaymentModal: {
      show: false,
      loading: false,
      source: null,
      object: null
    },
  },
  actions: {
    initTableData({state}) {
      state.tableData = {
        sale: getDefaultTableData(),
        customer: getDefaultTableData(),
        invoices: getDefaultTableData(),
        shipping: getDefaultTableData()
      }
      return true
    },
    resetTableDataPaginationPage({state}) {
      state.tableData[state.tableSource].pagination.page = 1;
    },
    async getTableParams({state}, payload = null) {
      let data;
      let currentTableData = state.tableData[state.tableSource];
      const ordering = await store.dispatch('global/getTableDataOrdering', {
        tableData: currentTableData,
        tableName: `${INVOICE_TABLE_NAME}.${state.tableSource}`
      });

      if (payload) {
        data = {
          ...payload.pagination,
          search: payload.search,
          ordering: payload.ordering,
        };
      } else {
        data = {
          ...currentTableData.pagination,
          search: currentTableData.search,
          ordering: ordering,
        }
      }

      if (currentTableData.searchModel) {
        const searchModelData = currentTableData.searchModel.toJSON();
        data = {
          ...data,
          ...searchModelData
        }
      }

      //Following logic here is because of double filter, from extra filters and user interaction filters
      let type = null
      if (data.type && data.type.split(',').length === 1) {
        type = data.type;
      }

      let shippingStatus = null
      if (data.shipping_status && data.shipping_status.split(',').length === 1) {
        shippingStatus = data.shipping_status;
      }

      if (currentTableData.extraFilters) {
        data = {...data, ...currentTableData.extraFilters};
      }

      if (type) {
        data.type = type;
      }

      if (shippingStatus) {
        data.shipping_status = shippingStatus;
      }

      const defaultExpand = currentTableData.expand
      const expand = currentTableData.toggleColumns.fields
        .filter(c => currentTableData.toggleColumns.defaultFields.includes(c.key) && c.expand)
        .map(c => c.expand)

      data.expand = [... new Set([...defaultExpand, ...expand])].join(',');

      return data;
    },
    async getTableData({commit, dispatch}, payload) {
      let params = await dispatch('getTableParams', payload);

      EventBus.$emit(ON_INVOICES_TABLE_GET_TABLE_DATA, {params});

      commit('updateTableLoading', true);
      const response = await InvoicesService.getInvoices(params);
      commit('updateTableLoading', false);
      if (response.success) {
        commit('updateTableData', {
          rows: response.body.results,
          pagination: {limit: params.limit, page: params.page, total: response.body.count},
          ordering: params.ordering,
          search: params.search
        });
      }
      return response;
    },
    showModal({commit}, {object = null, source, sale, creditNoteWithoutRefundParams} = {}) {
      commit('showModal', {object: _.cloneDeep(object), source, sale: _.cloneDeep(sale), creditNoteWithoutRefundParams});
    },
    async getModalObject({commit}, {uuid, removeOldData = false}) {
      let expand = [
        'bidder.customer.identification',
        'bidder.customer.billing_address',
        'miscellaneous_items',
        'bidder.lots.financial_item.buyer_financial_item_charges.charge',
        'bidder.lots.inventory.inventory_miscellaneous_items',
        'auction',
        'invoice_shipping_address.country',
        'invoice_billing_address.country',
      ]
      let response = await InvoicesService.getInvoice(uuid, {
        expand: expand.join(',')
      });
      if (response.success) {
        if (removeOldData) {
          delete response.body.created_at;
          delete response.body.date;
          delete response.body.is_processed;
          delete response.body.is_not_editable;
          delete response.body.is_legacy;
        }
        commit('updateModalObject', response.body);
      }
      return response;
    },
    async getManualInvoiceModalObject({commit}, {uuid, removeOldData = false}) {
      let response = await InvoicesService.getInvoice(uuid, {
        expand: [
          'customer.identification',
          'currency',
          'financial_items.inventory',
          'miscellaneous_items',
          'financial_items.buyer_financial_item_charges.charge',
          'invoice_shipping_address.country',
          'invoice_billing_address.country',
        ].join(',')
      });
      if (response.success) {
        if (removeOldData) {
          delete response.body.created_at;
          delete response.body.date;
          delete response.body.is_processed;
          delete response.body.is_not_editable;
          delete response.body.is_legacy;
        }
        commit('getManualInvoiceModalObject', response.body);
      }
      return response;
    },
    async getServiceInvoiceModalObject({commit}, {uuid, removeOldData = false}) {
      let response = await InvoicesService.getInvoice(uuid, {
        expand: [
          'customer',
          'miscellaneous_items',
          'currency',
          'invoice_billing_address.country',
        ].join(',')
      });
      if (response.success) {
        if (removeOldData) {
          delete response.body.created_at;
          delete response.body.date;
          delete response.body.is_processed;
          delete response.body.is_not_editable;
          delete response.body.is_legacy;
        }
        commit('getServiceInvoiceModalObject', response.body);
      }
      return response;
    },
    async getDepositInvoiceModalObject({state}, {uuid, removeOldData = false}) {
      let response = await InvoicesService.getInvoice(uuid, {
        expand: [
          'customer',
          'miscellaneous_items',
          'currency',
          'invoice_billing_address.country',
        ].join(',')
      });
      if (response.success) {
        if (removeOldData) {
          delete response.body.created_at;
          delete response.body.date;
          delete response.body.is_processed;
          delete response.body.is_not_editable;
          delete response.body.is_legacy;
        }
        state.depositInvoiceModal.object = response.body
      }
      return response;
    },
    async getShippingModalData({commit}, invoice) {
      let expand = '';
      if (invoice.type === INVOICE_TYPE_CREDIT_NOTE) {
        expand = 'credited_invoice.customer.billing_address,';
        if (invoice.credited_invoice.type === INVOICE_TYPE_AUCTION) {
          expand += 'credited_invoice.credited_financial_items.lot.reservation.inventory.item_location';
        } else if (invoice.credited_invoice.type === INVOICE_TYPE_MANUAL) {
          expand += 'credited_invoice.credited_financial_items.inventory.item_location';
        }
      } else {
        const financialItemExpand = invoice.is_credited ? 'credited_financial_items' : ['cancelled'].includes(invoice.status) ? 'cancelled_financial_items' : 'financial_items';
        expand = 'customer.billing_address,';
        if (invoice.type === INVOICE_TYPE_AUCTION) {
          expand += `${financialItemExpand}.lot.reservation.inventory.item_location`;
        } else if (invoice.type === INVOICE_TYPE_MANUAL) {
          expand += `${financialItemExpand}.inventory.item_location`;
        }
      }
      let response = await InvoicesService.getInvoice(invoice.uuid, {
        expand
      });
      if (response.success) {
        commit('getShippingModalData', response.body);
      }
      return response;
    },
    hideModal({commit}) {
      commit('hideModal');
    },
    showPaymentModal({commit}, {object = null, source, invoices, type = 'payment'} = {}) {
      commit('showPaymentModal', {
        object: _.cloneDeep(object),
        invoices: _.cloneDeep(invoices),
        type,
        source,
      });
    },
    updatePaymentModalObject({state}, {object}) {
      state.paymentModal.object = _.cloneDeep(object);
    },
    hidePaymentModal({commit}) {
      commit('hidePaymentModal');
    },
    updatePaymentListModalInvoice({state}) {
      const invoiceNo = state.paymentListModal.invoice.invoice_no;
      let invoice = state.tableData[state.tableSource].rows.find(invoice => invoice.invoice_no === invoiceNo);
      if (invoice) {
        state.paymentListModal.invoice = invoice;
      }
      return true;
    },
    showPaymentListModal({commit}, {source, invoice} = {}) {
      commit('showPaymentListModal', {
        invoice: _.cloneDeep(invoice),
        source,
      });
    },
    hidePaymentListModal({commit}) {
      commit('hidePaymentListModal');
    },
    async getMarkItemsCollectedModalObject({commit}, {invoice}) {
      let expand = ''

      if (invoice.type === INVOICE_TYPE_AUCTION) {
        expand = 'bidder.customer,bidder.lots.financial_item,bidder.lots.inventory'
      } else if (invoice.type === INVOICE_TYPE_MANUAL) {
        expand = 'customer,currency,financial_items.inventory,miscellaneous_items'
      }

      let response = await InvoicesService.getInvoice(invoice.uuid, {expand});
      if (response.success) {
        commit('getMarkItemsCollectedModalObject', response.body);
      }
      return response;
    },
    showMarkItemsCollectedModal({commit}, {source} = {}) {
      commit('showMarkItemsCollectedModal', {
        source,
      });
    },
    hideMarkItemsCollectedModal({commit}) {
      commit('hideMarkItemsCollectedModal');
    },
    async updateTableSource({state}, {extraFilters, source, sale, customer = null}) {
      state.tableSource = source;
      state.currentSale = _.cloneDeep(sale);
      state.tableData[source].extraFilters = extraFilters;
      state.tableData[source].customer = _.cloneDeep(customer);
    },
    updateSearchModel({commit, state}, {searchModel, source = null}) {
      commit('updateSearchModel', {
        searchModel: new InvoiceSearch({
          ...state.tableData[source || state.tableSource].searchModel,
          ...searchModel
        }),
        source
      });
    },
    resetSearchModel({commit}) {
      commit('resetSearchModel')
    },
    async resetTable({state, dispatch}) {
      const extraFilters = state.tableData[state.tableSource].extraFilters;
      const tmpToggleColumns = _.cloneDeep(state.tableData[state.tableSource].toggleColumns);
      let defaultTableData = getDefaultTableData();
      defaultTableData.pagination.limit = state.tableData[state.tableSource].pagination.limit;
      state.tableData[state.tableSource] = defaultTableData;
      state.tableData[state.tableSource].toggleColumns = tmpToggleColumns;
      state.tableData[state.tableSource].extraFilters = extraFilters;

      dispatch('getTableData');
    },
    showBulkEditInvoicesModal({commit}, {invoices}){
      commit('showBulkEditInvoicesModal', {invoices})
    },
    hideBulkEditInvoicesModal({commit}){
      commit('hideBulkEditInvoicesModal')
    },
    showManualInvoiceModal({commit}, {object = null, customer = null, singleInventoryItem = null, source, creditNoteWithoutRefundParams = null} = {}) {
      commit('showManualInvoiceModal', {object: _.cloneDeep(object), source, singleInventoryItem, customer, creditNoteWithoutRefundParams});
    },
    hideManualInvoiceModal({commit}) {
      commit('hideManualInvoiceModal');
    },
    showDepositInvoiceModal({commit}, {object = null, customer = null, source, creditNoteWithoutRefundParams = null} = {}) {
      commit('showDepositInvoiceModal', {object: _.cloneDeep(object), source, customer, creditNoteWithoutRefundParams});
    },
    hideDepositInvoiceModal({commit}) {
      commit('hideDepositInvoiceModal');
    },
    showShippingModal({commit}, {invoice} = {}) {
      commit('showShippingModal', {invoice: _.cloneDeep(invoice)});
    },
    hideShippingModal({commit}) {
      commit('hideShippingModal');
    },
    async markAllItemsCollected({dispatch}, payload) {
      let response = await InvoicesService.markAllItemsCollected(payload);
      if (response.success) {
        dispatch('getTableData')
      }
      return response;
    },
    showContinuousAuctionInvoiceModal({commit}, {object = null, source, sale = null, creditNoteWithoutRefundParams = null} = {}) {
      commit('showContinuousAuctionInvoiceModal', {object: _.cloneDeep(object), source, sale, creditNoteWithoutRefundParams});
    },
    hideContinuousAuctionInvoiceModal({commit}) {
      commit('hideContinuousAuctionInvoiceModal');
    },
    async updateToggleColumns({commit, dispatch}, payload) {
      if (payload.isSaveAction) {
        commit('updateTableLoading', true);
        await commit('updateToggleColumns', payload.selectedColumns);
        await dispatch('getTableData');
        commit('updateTableLoading', false);
      } else {
        await commit('updateToggleColumns', payload.selectedColumns);
      }
    },
    async getCreditNoteInvoiceModalObject({commit}, {invoice}) {
      let expand = '';
      if (invoice.type === INVOICE_TYPE_CREDIT_NOTE) {
        expand = 'credited_invoice.currency,credited_invoice,credited_invoice.outstanding_amount,credited_invoice.paid_amount,credited_invoice.customer.billing_address.country,';
        if (invoice.credited_invoice.type === INVOICE_TYPE_AUCTION) {
          expand += 'credited_invoice.credited_financial_items.lot.reservation.inventory';
        } else if (invoice.credited_invoice.type === INVOICE_TYPE_MANUAL) {
          expand += 'credited_invoice.credited_financial_items.inventory';
        }
      } else {
        expand = 'currency,total_amount,outstanding_amount,customer.billing_address.country,auction,';
        if (invoice.type === INVOICE_TYPE_AUCTION) {
          expand += 'financial_items.lot.reservation.inventory';
        } else if (invoice.type === INVOICE_TYPE_MANUAL) {
          expand += 'financial_items.inventory';
        }
      }
      let response = await InvoicesService.getInvoice(invoice.uuid, {expand});
      if (response.success) {
        commit('getCreditNoteInvoiceModalObject', {object: response.body});
      }
      return response;
    },
    showCreditNoteModal({commit}, {object = null, source} = {}) {
      commit('showCreditNoteModal', {object: _.cloneDeep(object), source});
    },
    hideCreditNoteModal({commit}) {
      commit('hideCreditNoteModal');
    },
    async createCreditNote({dispatch}, params) {
      const response = await InvoicesService.createCreditNote(params);
      if (response.success) {
        dispatch('getTableData');
      }
      return response;
    },
    async updateCreditNote({dispatch}, params) {
      const response = await InvoicesService.updateCreditNote(params);
      if (response.success) {
        dispatch('getTableData');
      }
      return response;
    },
    showServiceInvoiceModal({commit}, {object = null, customer = null, source, creditNoteWithoutRefundParams = null} = {}) {
      commit('showServiceInvoiceModal', {object: _.cloneDeep(object), source, customer, creditNoteWithoutRefundParams});
    },
    hideServiceInvoiceModal({commit}) {
      commit('hideServiceInvoiceModal');
    },


    async showTransactionExportsModal({commit}, {invoice = null, source} = {}) {
      commit('showTransactionExportsModal', {invoice: _.cloneDeep(invoice), source});
    },
    async hideTransactionExportsModal({commit}) {
      commit('hideTransactionExportsModal');
    },
    async showPaymentExportsModal({commit}, {invoice = null, source} = {}) {
      commit('showPaymentExportsModal', {invoice: _.cloneDeep(invoice), source});
    },
    async hidePaymentExportsModal({commit}) {
      commit('hidePaymentExportsModal');
    },
    showCancelInvoiceModal({commit}, payload) {
      commit('showCancelInvoiceModal', payload);
    },
    hideCancelInvoiceModal({commit}) {
      commit('hideCancelInvoiceModal');
    },
    async showAllocatePaymentModal({state}, {invoiceUuid, source}) {
      state.allocatePaymentModal.show = true
      state.allocatePaymentModal.source = source
      state.allocatePaymentModal.loading = true
      let response = await InvoicesService.getInvoice(invoiceUuid, {expand: 'customer.billing_address,total_amount,paid_amount,outstanding_amount,auction,currency'})
      if (response.success) {
        state.allocatePaymentModal.sourceInvoice = response.body
      }
      state.allocatePaymentModal.loading = false
    },
    hideAllocatePaymentModal({state}) {
      state.allocatePaymentModal.show = false
      state.allocatePaymentModal.sourceInvoice = null
      state.allocatePaymentModal.source = null
    },
    async submitAllocatePayment({state}, payload) {
      state.allocatePaymentModal.loading = true
      let response = await InvoicesService.allocatePayment(payload)
      state.allocatePaymentModal.loading = false
      return response
    },
    showInvoiceBatchPaymentModal({state}, {source}) {
      state.invoiceBatchPaymentModal.show = true
      state.invoiceBatchPaymentModal.source = source
    },
    hideInvoiceBatchPaymentModal({state}) {
      state.invoiceBatchPaymentModal.show = false
      state.invoiceBatchPaymentModal.source = null
      state.invoiceBatchPaymentModal.object = null
    },
    toggleInvoiceBatchPaymentModalLoading({state}, val) {
      state.invoiceBatchPaymentModal.loading = val
    },
    async getInvoiceBatchPaymentModalObject({state}, batchPaymentUuid) {
      let response = await InvoicesService.getBatchPayment(batchPaymentUuid, {
        expand: [
          'payments.invoice_payments.invoice.currency',
          'payments.invoice_payments.invoice.customer.deposits.currency',
          'payments.invoice_payments.invoice.customer.deposit_reservations.auction',
          'payments.invoice_payments.invoice.customer.deposit_reservations.currency',
        ].join(',')
      })
      if (response.success) {
        state.invoiceBatchPaymentModal.object = response.body
      }
    }
  },
  mutations: {
    updateTableLoading: (state, payload) => state.tableData[state.tableSource].loading = payload,
    updateTableData: (state, payload) => state.tableData[state.tableSource] = {
      ...state.tableData[state.tableSource],
      ...payload
    },
    showModal: (state, {object, source, sale, creditNoteWithoutRefundParams}) => {
      state.modal.show = true;
      state.modal.object = object;
      state.modal.source = source;
      state.modal.sale = sale;
      state.modal.creditNoteWithoutRefundParams = creditNoteWithoutRefundParams;
    },
    updateModalObject: (state, object) => {
      if (store.state.global.enableContinuousAuction) {
        state.continuousAuctionInvoiceModal.object = object;
      } else {
        state.modal.object = object;
      }
    },
    getManualInvoiceModalObject: (state, object) => state.manualInvoiceModal.object = object,
    getServiceInvoiceModalObject: (state, object) => state.serviceInvoiceModal.object = object,
    getMarkItemsCollectedModalObject: (state, object) => state.markItemsCollectedModal.object = object,
    getShippingModalData: (state, object) => state.shippingModal.object = object,
    hideModal: state => state.modal.show = false,
    showPaymentModal: (state, {object, source, type, invoices}) => {
      state.paymentModal.show = true;
      state.paymentModal.object = object;
      state.paymentModal.source = source;
      state.paymentModal.type = type;
      state.paymentModal.invoices = invoices;
    },
    hidePaymentModal: state => state.paymentModal.show = false,
    showPaymentListModal: (state, {source, invoice}) => {
      state.paymentListModal.show = true;
      state.paymentListModal.source = source;
      state.paymentListModal.invoice = invoice;
    },
    hidePaymentListModal: state => state.paymentListModal.show = false,
    showMarkItemsCollectedModal: (state) => state.markItemsCollectedModal.show = true,
    hideMarkItemsCollectedModal: state => state.markItemsCollectedModal.show = false,
    updateSearchModel: (state, {
      searchModel,
      source
    }) => state.tableData[source || state.tableSource].searchModel = searchModel,
    showManualInvoiceModal: (state, {object, source, customer, singleInventoryItem, creditNoteWithoutRefundParams}) => {
      state.manualInvoiceModal.show = true;
      state.manualInvoiceModal.object = object;
      state.manualInvoiceModal.source = source;
      state.manualInvoiceModal.customer = customer;
      state.manualInvoiceModal.singleInventoryItem = singleInventoryItem;
      state.manualInvoiceModal.creditNoteWithoutRefundParams = creditNoteWithoutRefundParams;
    },
    hideManualInvoiceModal: state => {
      state.manualInvoiceModal = getDefaultManualInvoiceModel();
    },
    showDepositInvoiceModal: (state, {object, source, customer, creditNoteWithoutRefundParams}) => {
      state.depositInvoiceModal.show = true;
      state.depositInvoiceModal.object = object;
      state.depositInvoiceModal.source = source;
      state.depositInvoiceModal.customer = customer;
      state.depositInvoiceModal.creditNoteWithoutRefundParams = creditNoteWithoutRefundParams;
    },
    hideDepositInvoiceModal: state => {
      state.depositInvoiceModal = getDefaultDepositInvoiceModel();
    },
    showShippingModal: (state, {invoice}) => {
      state.shippingModal.show = true;
      state.shippingModal.object = invoice;
    },
    hideShippingModal: state => {
      state.shippingModal.object = null;
      state.shippingModal.show = false;
    },
    showContinuousAuctionInvoiceModal: (state, {object, source, sale, creditNoteWithoutRefundParams}) => {
      state.continuousAuctionInvoiceModal.show = true;
      state.continuousAuctionInvoiceModal.object = object;
      state.continuousAuctionInvoiceModal.source = source;
      state.continuousAuctionInvoiceModal.sale = sale;
      state.continuousAuctionInvoiceModal.creditNoteWithoutRefundParams = creditNoteWithoutRefundParams;
    },
    hideContinuousAuctionInvoiceModal: state => {
      state.continuousAuctionInvoiceModal = getDefaultContinuousAuctionInvoiceModal();
    },
    updateToggleColumns: (state, selectedColumns) => {
      state.tableData[state.tableSource].toggleColumns.defaultFields = [...selectedColumns];
      let selectedFields = [];
      let notSelectedFields = [];
      selectedColumns.forEach(s => {
        selectedFields.push({
          ...state.tableData[state.tableSource].toggleColumns.fields.find(f => f.key === s),
          isVisible: true
        });
      });
      state.tableData[state.tableSource].toggleColumns.fields.forEach(f => {
        if (!selectedColumns.includes(f.key)) {
          notSelectedFields.push({...f, isVisible: false});
        }
      });
      state.tableData[state.tableSource].toggleColumns.fields = [...selectedFields, ...notSelectedFields];
    },
    resetSearchModel: state => {
      state.tableData[state.tableSource].searchModel = new InvoiceSearch()
    },
    showCreditNoteModal: (state, {object, source}) => {
      state.creditNoteModal.show = true;
      state.creditNoteModal.object = object;
      state.creditNoteModal.source = source;
    },
    hideCreditNoteModal: state => {
      state.creditNoteModal = getDefaultCreditNoteModel();
    },
    getCreditNoteInvoiceModalObject: (state, {object}) => {
      state.creditNoteModal.object = object;
    },
    showServiceInvoiceModal: (state, {object, source, customer, creditNoteWithoutRefundParams}) => {
      state.serviceInvoiceModal.show = true;
      state.serviceInvoiceModal.object = object;
      state.serviceInvoiceModal.source = source;
      state.serviceInvoiceModal.customer = customer;
      state.serviceInvoiceModal.creditNoteWithoutRefundParams = creditNoteWithoutRefundParams;
    },
    hideServiceInvoiceModal: state => {
      state.serviceInvoiceModal = getDefaultServiceInvoiceModal();
    },
    showTransactionExportsModal: (state, {source, invoice}) => {
      state.transactionExportsModal.show = true
      state.transactionExportsModal.source = source
      state.transactionExportsModal.invoice = invoice
    },
    hideTransactionExportsModal: (state) => {
      state.transactionExportsModal.show = false
      state.transactionExportsModal.source = null
      state.transactionExportsModal.invoice = null
    },
    showPaymentExportsModal: (state, {source, invoice}) => {
      state.paymentExportsModal.show = true
      state.paymentExportsModal.source = source
      state.paymentExportsModal.invoice = invoice
    },
    hidePaymentExportsModal: (state) => {
      state.paymentExportsModal.show = false
      state.paymentExportsModal.source = null
      state.paymentExportsModal.invoice = null
    },
    showCancelInvoiceModal(state, payload) {
      const {source, selectedItems} = payload
      state.cancelInvoiceModal.show = true
      state.cancelInvoiceModal.source = source
      state.cancelInvoiceModal.selectedItems = selectedItems
    },
    hideCancelInvoiceModal(state) {
      state.cancelInvoiceModal.show = false
      state.cancelInvoiceModal.source = null
      state.cancelInvoiceModal.selectedItems = null
    },
    showBulkEditInvoicesModal(state, {invoices}) {
      state.bulkEditInvoicesModal.invoices = invoices
      state.bulkEditInvoicesModal.show = true
    },
    hideBulkEditInvoicesModal(state) {
      state.bulkEditInvoicesModal = getDefaultBulkEditInvoicesModel()
    }
  }
}
